import { create } from "zustand";
import { immer } from "zustand/middleware/immer";
import { toast } from "react-toastify";

import { loginService } from "@/services/login";
import type { ApiEnvelope } from "@/types/api";

export interface SecurityImage {
  securityImageId: string | number;
  path: string;
  imageName: string;
}

export interface UserSecurityImage {
  imageExists?: boolean;
  path?: string;
  [key: string]: unknown;
}

interface SecurityImageStore {
  images: SecurityImage[];
  selectedImage: string | number | null;
  securityImage: UserSecurityImage | null;
  error: unknown;
  getSecurityImages: () => Promise<void>;
  getSecurityUserImage: (systemUserId: string | number) => Promise<void>;
  selectSecurityImages: (imageId: string | number) => void;
  insertSecurityImages: (
    systemUserId: string | number,
    securityImageId: string | number | null
  ) => Promise<boolean>;
}

export const useSecurityImageStore = create<SecurityImageStore>()(
  immer((set, get) => ({
    images: [],
    selectedImage: null,
    securityImage: null,
    error: null,
    getSecurityImages: async () => {
      set({ error: null });

      try {
        const response = await loginService.getSecurityImages<ApiEnvelope<SecurityImage[]>>();

        if (response.data.header?.isSuccess) {
          set({ images: response.data.body || [] });
        } else {
          toast.error("Güvenlik resimleri alınamadı.");
          set({ error: response.data });
        }
      } catch (error) {
        set({ error });
      }
    },
    getSecurityUserImage: async (systemUserId) => {
      set({ error: null, securityImage: null });

      try {
        const response = await loginService.getSecurityUserImage<ApiEnvelope<UserSecurityImage>>({
          systemUserId,
        });

        if (response.data.header?.isSuccess && response.data.body?.imageExists) {
          set({ securityImage: response.data.body });
        } else {
          set({ securityImage: null });
        }
      } catch (error) {
        toast.error("Güvenlik resmi alınırken bir hata oluştu.");
        set({ error });
      }
    },
    selectSecurityImages: (imageId) => {
      set((state) => ({ ...state, selectedImage: imageId }));
    },
    insertSecurityImages: async (systemUserId, securityImageId) => {
      if (!securityImageId) {
        toast.error("Lütfen bir güvenlik resmi seçin.");

        return false;
      }

      try {
        const response = await loginService.insertSecurityImages<ApiEnvelope<unknown>>({
          systemUserId,
          securityImageId,
        });

        if (response.data.header?.isSuccess) {
          get().getSecurityUserImage(systemUserId);

          return true;
        }

        toast.error("Güvenlik resmi kaydedilirken hata oluştu.");

        return false;
      } catch {
        toast.error("Bir hata oluştu, lütfen tekrar deneyin.");

        return false;
      }
    },
  }))
);
